import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  BookOpen,
  FlaskConical,
  Calculator,
  CalendarDays,
  Rocket,
  ArrowRight,
  Beaker,
  Microscope,
  TestTube2,
  Atom,
} from "lucide-react";
import { PageNavigation } from "@/components/PageNavigation";

// Feature cards data with translation keys
const FEATURES = [
  {
    key: "whatArePeptides",
    icon: Atom,
    path: "/what-are-peptides",
    color: "from-cyan-500/10 to-teal-500/10",
    iconColor: "text-cyan-600",
  },
  {
    key: "learnPeptides",
    icon: BookOpen,
    path: "/learn",
    color: "from-blue-500/10 to-cyan-500/10",
    iconColor: "text-blue-600",
  },
  {
    key: "reconstitution",
    icon: FlaskConical,
    path: "/reconstitution",
    color: "from-emerald-500/10 to-teal-500/10",
    iconColor: "text-emerald-600",
  },
  {
    key: "calculator",
    icon: Calculator,
    path: "/calculator",
    color: "from-sky-500/10 to-blue-500/10",
    iconColor: "text-sky-600",
  },
  {
    key: "schedule",
    icon: CalendarDays,
    path: "/schedule",
    color: "from-amber-500/10 to-orange-500/10",
    iconColor: "text-amber-600",
  },
  {
    key: "getStarted",
    icon: Rocket,
    path: "/get-started",
    color: "from-rose-500/10 to-pink-500/10",
    iconColor: "text-rose-600",
  },
];

// Molecular/Chemistry Lab illustration component - no syringe
const LabIllustration = () => (
  <div className="relative w-full max-w-lg mx-auto">
    <svg viewBox="0 0 400 280" className="w-full h-auto" xmlns="http://www.w3.org/2000/svg">
      {/* Background molecules - decorative */}
      <g opacity="0.15">
        {/* Large background hexagon */}
        <polygon points="320,50 360,75 360,125 320,150 280,125 280,75" fill="none" stroke="hsl(var(--primary))" strokeWidth="2"/>
        <polygon points="60,180 90,200 90,240 60,260 30,240 30,200" fill="none" stroke="hsl(var(--primary))" strokeWidth="1.5"/>
      </g>

      {/* Main Erlenmeyer Flask */}
      <g transform="translate(130, 40)">
        <path d="M50 0 L50 70 L15 160 Q8 175 25 180 L115 180 Q132 175 125 160 L90 70 L90 0 Z" 
              fill="white" stroke="hsl(var(--primary))" strokeWidth="2"/>
        {/* Liquid */}
        <path d="M30 140 Q25 160 35 165 L105 165 Q115 160 110 140 L85 85 L55 85 Z" 
              fill="hsl(var(--primary))" opacity="0.15"/>
        {/* Bubbles */}
        <circle cx="55" cy="130" r="4" fill="hsl(var(--primary))" opacity="0.25"/>
        <circle cx="75" cy="145" r="3" fill="hsl(var(--primary))" opacity="0.25"/>
        <circle cx="90" cy="125" r="2.5" fill="hsl(var(--primary))" opacity="0.25"/>
        <circle cx="65" cy="150" r="2" fill="hsl(var(--primary))" opacity="0.25"/>
      </g>

      {/* Beaker */}
      <g transform="translate(280, 100)">
        <rect x="0" y="10" width="60" height="80" rx="3" fill="white" stroke="hsl(var(--border))" strokeWidth="1.5"/>
        {/* Spout */}
        <path d="M0 10 L-8 20 L0 20" fill="white" stroke="hsl(var(--border))" strokeWidth="1.5"/>
        {/* Measurement lines */}
        <line x1="5" y1="30" x2="15" y2="30" stroke="hsl(var(--muted-foreground))" strokeWidth="0.5"/>
        <line x1="5" y1="50" x2="15" y2="50" stroke="hsl(var(--muted-foreground))" strokeWidth="0.5"/>
        <line x1="5" y1="70" x2="15" y2="70" stroke="hsl(var(--muted-foreground))" strokeWidth="0.5"/>
        {/* Liquid */}
        <rect x="3" y="55" width="54" height="32" rx="2" fill="hsl(201 59% 70%)" opacity="0.2"/>
      </g>

      {/* Test Tubes in Rack */}
      <g transform="translate(40, 90)">
        {/* Rack */}
        <rect x="0" y="60" width="70" height="8" rx="2" fill="hsl(var(--muted))" stroke="hsl(var(--border))" strokeWidth="1"/>
        <rect x="5" y="68" width="8" height="30" fill="hsl(var(--muted))"/>
        <rect x="57" y="68" width="8" height="30" fill="hsl(var(--muted))"/>
        {/* Tubes */}
        <rect x="12" y="15" width="12" height="50" rx="6" fill="white" stroke="hsl(var(--border))" strokeWidth="1"/>
        <rect x="29" y="10" width="12" height="55" rx="6" fill="white" stroke="hsl(var(--border))" strokeWidth="1"/>
        <rect x="46" y="20" width="12" height="45" rx="6" fill="white" stroke="hsl(var(--border))" strokeWidth="1"/>
        {/* Liquids in tubes */}
        <rect x="14" y="40" width="8" height="22" rx="4" fill="hsl(var(--primary))" opacity="0.2"/>
        <rect x="31" y="35" width="8" height="27" rx="4" fill="hsl(201 59% 60%)" opacity="0.2"/>
        <rect x="48" y="45" width="8" height="17" rx="4" fill="hsl(150 50% 50%)" opacity="0.2"/>
      </g>

      {/* Vial */}
      <g transform="translate(320, 170)">
        <rect x="5" y="8" width="30" height="60" rx="4" fill="white" stroke="hsl(var(--border))" strokeWidth="1.5"/>
        <rect x="8" y="0" width="24" height="10" rx="2" fill="hsl(var(--primary))"/>
        <rect x="8" y="45" width="24" height="20" rx="3" fill="hsl(var(--primary))" opacity="0.15"/>
      </g>

      {/* Molecular structures */}
      {/* Molecule 1 - Hexagon with branches */}
      <g transform="translate(200, 5)" opacity="0.6">
        <polygon points="25,0 45,12 45,36 25,48 5,36 5,12" fill="none" stroke="hsl(var(--primary))" strokeWidth="1.5"/>
        <circle cx="25" cy="0" r="4" fill="hsl(var(--primary))"/>
        <circle cx="45" cy="12" r="4" fill="hsl(var(--primary))"/>
        <circle cx="45" cy="36" r="4" fill="hsl(var(--primary))"/>
        <circle cx="25" cy="48" r="4" fill="hsl(var(--primary))"/>
        <circle cx="5" cy="36" r="4" fill="hsl(var(--primary))"/>
        <circle cx="5" cy="12" r="4" fill="hsl(var(--primary))"/>
        {/* Branch */}
        <line x1="25" y1="0" x2="25" y2="-15" stroke="hsl(var(--primary))" strokeWidth="1.5"/>
        <circle cx="25" cy="-18" r="3" fill="hsl(201 59% 55%)"/>
      </g>

      {/* Molecule 2 - Linear chain */}
      <g transform="translate(10, 40)" opacity="0.5">
        <circle cx="0" cy="0" r="5" fill="hsl(var(--primary))"/>
        <line x1="5" y1="0" x2="20" y2="0" stroke="hsl(var(--primary))" strokeWidth="1.5"/>
        <circle cx="25" cy="0" r="4" fill="hsl(201 59% 55%)"/>
        <line x1="29" y1="0" x2="44" y2="0" stroke="hsl(var(--primary))" strokeWidth="1.5"/>
        <circle cx="49" cy="0" r="3" fill="hsl(var(--primary))"/>
      </g>

      {/* Molecule 3 - Branch structure */}
      <g transform="translate(350, 40)" opacity="0.5">
        <circle cx="15" cy="20" r="5" fill="hsl(var(--primary))"/>
        <line x1="15" y1="15" x2="15" y2="0" stroke="hsl(var(--primary))" strokeWidth="1.5"/>
        <circle cx="15" cy="-3" r="3" fill="hsl(201 59% 55%)"/>
        <line x1="10" y1="23" x2="0" y2="35" stroke="hsl(var(--primary))" strokeWidth="1.5"/>
        <circle cx="-3" cy="38" r="3" fill="hsl(var(--primary))"/>
        <line x1="20" y1="23" x2="30" y2="35" stroke="hsl(var(--primary))" strokeWidth="1.5"/>
        <circle cx="33" cy="38" r="3" fill="hsl(var(--primary))"/>
      </g>

      {/* Floating atoms/particles */}
      <circle cx="180" cy="250" r="4" fill="hsl(var(--primary))" opacity="0.3"/>
      <circle cx="250" cy="260" r="3" fill="hsl(201 59% 55%)" opacity="0.3"/>
      <circle cx="310" cy="245" r="2" fill="hsl(var(--primary))" opacity="0.3"/>
      <circle cx="100" cy="255" r="3" fill="hsl(var(--primary))" opacity="0.25"/>
      
      {/* DNA-like helix hint */}
      <g transform="translate(370, 120)" opacity="0.4">
        <path d="M0 0 Q10 10, 0 20 Q-10 30, 0 40 Q10 50, 0 60" 
              stroke="hsl(var(--primary))" strokeWidth="1.5" fill="none"/>
        <path d="M8 0 Q-2 10, 8 20 Q18 30, 8 40 Q-2 50, 8 60" 
              stroke="hsl(201 59% 55%)" strokeWidth="1.5" fill="none"/>
      </g>
    </svg>
  </div>
);

export const HomePage = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <div className="w-full py-12 md:py-20 px-4 bg-gradient-to-b from-primary/5 via-primary/3 to-background">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            {/* Text Content */}
            <div className="text-center lg:text-left">
              <div className="flex items-center justify-center lg:justify-start gap-2 mb-4">
                <Atom className="w-5 h-5 text-primary" />
                <span className="text-sm font-medium text-primary uppercase tracking-wider">
                  {t('home.researchEducation')}
                </span>
              </div>
              
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4 leading-tight">
                {t('home.welcomeTo')}<br />
                <span className="text-primary">{t('home.brandName')}</span>
              </h1>
              
              <p className="text-lg text-muted-foreground mb-6 leading-relaxed max-w-xl mx-auto lg:mx-0">
                {t('home.heroDescription')}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                <Link to="/what-are-peptides">
                  <Button size="lg" className="gap-2 w-full sm:w-auto">
                    <BookOpen className="w-4 h-4" />
                    {t('home.startLearning')}
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/get-started">
                  <Button size="lg" variant="outline" className="gap-2 w-full sm:w-auto">
                    <Rocket className="w-4 h-4" />
                    {t('nav.getStarted')}
                  </Button>
                </Link>
              </div>
            </div>
            
            {/* Illustration */}
            <div className="hidden lg:block">
              <LabIllustration />
            </div>
          </div>
        </div>
      </div>

      {/* Stats/Trust Banner */}
      <div className="w-full py-8 px-4 border-y border-border/50 bg-muted/20">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="flex justify-center mb-2">
                <TestTube2 className="w-6 h-6 text-primary" />
              </div>
              <p className="text-2xl font-bold text-foreground">11+</p>
              <p className="text-sm text-muted-foreground">{t('home.peptidesCovered')}</p>
            </div>
            <div>
              <div className="flex justify-center mb-2">
                <Beaker className="w-6 h-6 text-primary" />
              </div>
              <p className="text-2xl font-bold text-foreground">5</p>
              <p className="text-sm text-muted-foreground">{t('home.freeTools')}</p>
            </div>
            <div>
              <div className="flex justify-center mb-2">
                <Microscope className="w-6 h-6 text-primary" />
              </div>
              <p className="text-2xl font-bold text-foreground">100%</p>
              <p className="text-sm text-muted-foreground">{t('home.researchBased')}</p>
            </div>
            <div>
              <div className="flex justify-center mb-2">
                <Rocket className="w-6 h-6 text-primary" />
              </div>
              <p className="text-2xl font-bold text-foreground">7/7</p>
              <p className="text-sm text-muted-foreground">{t('home.daysSupport')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="max-w-6xl mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
            {t('home.exploreTools')}
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            {t('home.exploreToolsDesc')}
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <Link key={index} to={feature.path}>
                <Card className="h-full hover:shadow-lg transition-all duration-300 hover:border-primary/30 group overflow-hidden">
                  <CardContent className="p-6">
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                      <Icon className={`w-7 h-7 ${feature.iconColor}`} />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                      {t(`features.${feature.key}.title`)}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      {t(`features.${feature.key}.description`)}
                    </p>
                    <div className="flex items-center text-sm font-medium text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                      {t('common.explore')} <ArrowRight className="w-4 h-4 ml-1" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      </div>

      {/* CTA Section */}
      <div className="w-full py-16 px-4 bg-primary text-primary-foreground">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            {t('home.readyToStart')}
          </h2>
          <p className="text-primary-foreground/80 mb-8 max-w-xl mx-auto">
            {t('home.readyToStartDesc')}
          </p>
          <Link to="/get-started">
            <Button size="lg" variant="secondary" className="gap-2">
              <Rocket className="w-4 h-4" />
              {t('home.getStartedToday')}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </div>

      {/* Page Navigation */}
      <PageNavigation currentPath="/" />

      {/* Disclaimer */}
      <div className="border-t border-border/50 bg-muted/20 py-6 px-4">
        <div className="max-w-6xl mx-auto">
          <p className="text-xs text-muted-foreground text-center leading-relaxed">
            <strong>{t('common.disclaimer')}</strong> {t('common.disclaimerText')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
